import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  useTheme,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  EmojiEvents as GrowthIcon,
  LocationOn as LocationIcon,
  GroupAdd as CommunityIcon,
  Handshake as PartnershipIcon,
  Instagram as InstagramIcon,
  Language as LanguageIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import { Link } from 'react-router-dom';
import PublicFooter from '../components/layout/PublicFooter';

const AboutUs = () => {
  const theme = useTheme();
  const history = useHistory();
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleNavigation = (path) => {
    history.push(path);
    handleMobileMenuClose();
  };

  const values = [
    {
      icon: <GrowthIcon sx={{ fontSize: 40, color: '#ff9800' }} />,
      title: 'Our Mission',
      description: 'To empower local businesses by connecting them with event opportunities, fostering growth, and building stronger communities across Canada.',
    },
    {
      icon: <LocationIcon sx={{ fontSize: 40, color: '#ff9800' }} />,
      title: 'Local Focus',
      description: 'We believe in the power of local business and community. Starting in Alberta, we\'re building a network that supports and celebrates local talent.',
    },
    {
      icon: <CommunityIcon sx={{ fontSize: 40, color: '#ff9800' }} />,
      title: 'Community First',
      description: 'We\'re not just a platform - we\'re a community. We bring together vendors and event organizers to create meaningful connections and lasting partnerships.',
    },
    {
      icon: <PartnershipIcon sx={{ fontSize: 40, color: '#ff9800' }} />,
      title: 'Partnership',
      description: 'We work closely with both vendors and event organizers to ensure our platform meets their needs and helps them succeed.',
    },
  ];

  return (
    <PublicLayout>
      {/* Minimal Header */}
      <AppBar 
        position="static" 
        sx={{ 
          bgcolor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 600,
              color: theme.palette.primary.main,
              cursor: 'pointer',
            }}
            onClick={() => history.push('/')}
          >
            Vendoroll
          </Typography>
          
          {/* Desktop Navigation */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center' }}>
            <Button
              color="primary"
              onClick={() => history.push('/')}
              sx={{
                color: '#ff9800',
                '&:hover': {
                  bgcolor: 'rgba(255, 152, 0, 0.04)',
                },
              }}
            >
              Home
            </Button>
            <Button
              color="primary"
              onClick={() => history.push('/about')}
              sx={{
                color: '#ff9800',
                '&:hover': {
                  bgcolor: 'rgba(255, 152, 0, 0.04)',
                },
              }}
            >
              About Us
            </Button>
            <Button
              color="primary"
              onClick={() => history.push('/contact')}
              sx={{
                color: '#ff9800',
                '&:hover': {
                  bgcolor: 'rgba(255, 152, 0, 0.04)',
                },
              }}
            >
              Contact
            </Button>
            <Button
              variant="contained"
              onClick={() => history.push('/#waitlist')}
              sx={{
                bgcolor: '#ff9800',
                '&:hover': {
                  bgcolor: '#e68a00',
                },
                borderRadius: 20,
                px: 3,
              }}
            >
              Join Waitlist
            </Button>
          </Box>
          
          {/* Mobile Navigation */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <Button
              variant="contained"
              onClick={() => history.push('/#waitlist')}
              sx={{
                bgcolor: '#ff9800',
                '&:hover': {
                  bgcolor: '#e68a00',
                },
                borderRadius: 20,
                px: 3,
                mr: 1,
              }}
            >
              Join Waitlist
            </Button>
            <IconButton
              color="primary"
              onClick={handleMobileMenuOpen}
              sx={{ color: '#ff9800' }}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={handleMobileMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={() => handleNavigation('/')}>Home</MenuItem>
              <MenuItem onClick={() => handleNavigation('/about')}>About Us</MenuItem>
              <MenuItem onClick={() => handleNavigation('/contact')}>Contact</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(45deg, #ff9800 30%, #f57c00 90%)`,
          color: 'white',
          py: { xs: 6, md: 8 },
          mb: 6,
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              mb: 2,
            }}
          >
            About Vendoroll
          </Typography>
          <Typography variant="h5" sx={{ maxWidth: '800px' }}>
            We're on a mission to transform how local businesses connect with event opportunities across Canada.
          </Typography>
        </Container>
      </Box>

      {/* Our Story Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={{
            color: '#ff9800',
            fontWeight: 700,
            mb: 4,
          }}
        >
          Our Story
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
          Vendoroll was born from a simple observation: local businesses and event organizers needed a better way to connect. 
          Founded in Calgary, Alberta, we set out to create a platform that would make it easier for vendors to find and apply 
          to events, and for organizers to discover and book amazing local talent.
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
          Today, we're proud to be building a community that supports local businesses and helps them grow. 
          Our platform is designed to be simple, efficient, and focused on what matters most - connecting great 
          vendors with great events.
        </Typography>
      </Container>

      {/* Our Values Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              color: '#ff9800',
              fontWeight: 700,
              mb: 6,
            }}
          >
            Our Values
          </Typography>
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={2}
                  sx={{
                    p: 3,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Box sx={{ mb: 2 }}>{value.icon}</Box>
                  <Typography variant="h6" gutterBottom>
                    {value.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {value.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Team Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Typography
          variant="h4"
          component="h2"
          gutterBottom
          sx={{
            color: '#ff9800',
            fontWeight: 700,
            mb: 6,
          }}
        >
          Our Team
        </Typography>
        <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
          We're a passionate team of entrepreneurs, developers, and community builders who believe in the power 
          of local business. Based in Calgary, we're working hard to create a platform that makes a real difference 
          in our community and beyond.
        </Typography>
      </Container>

      {/* Footer */}
      <PublicFooter />
    </PublicLayout>
  );
};

export default AboutUs; 